import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BedSelector, Selector, UnitSelector } from 'app/types/costAndFees.type';
import { useCostsAndFeesContext } from 'app/shared/modules/hdp/CostsAndFees/context/CostsAndFeesProvider';
import * as styles from './unitPicker.styles';

const getBedDisplayName = (count: number): string => {
  if (count === 0) return 'Studio';
  return `${count} beds`;
};

export const UnitPicker = () => {
  const { calculatedCostsAndFees, setSelectorOverrides, unitOptionsGrouped } = useCostsAndFeesContext();

  const [selectedBedCounts, setSelectedBedCounts] = useState<Array<number>>([]);
  const [allBedsSelected, setAllBedsSelected] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState<UnitSelector | null>(null);

  const bedOptions = useMemo(() => {
    if (!calculatedCostsAndFees?.selectors?.beds) return [];
    return calculatedCostsAndFees.selectors.beds
      .filter((bed: BedSelector) => bed.numBeds !== undefined)
      .sort((a, b) => a.numBeds - b.numBeds);
  }, [calculatedCostsAndFees?.selectors?.beds]);

  const bedSelectors = useMemo(
    () => calculatedCostsAndFees?.selectors?.beds || [],
    [calculatedCostsAndFees?.selectors?.beds],
  );

  const allUnits = useMemo(() => {
    if (unitOptionsGrouped && Object.keys(unitOptionsGrouped).length > 0) {
      return Object.values(unitOptionsGrouped).flat();
    }
    return calculatedCostsAndFees?.selectors?.units || [];
  }, [unitOptionsGrouped, calculatedCostsAndFees?.selectors?.units]);

  const filteredUnits = useMemo(() => {
    if (allBedsSelected) return allUnits;
    if (selectedBedCounts.length === 0) return [];
    return allUnits.filter((unit) => selectedBedCounts.includes(unit.numBeds));
  }, [allUnits, selectedBedCounts, allBedsSelected]);

  const selectAllBeds = useCallback(() => {
    setSelectedBedCounts([]);
    setAllBedsSelected(true);
    setSelectedUnit(null);
  }, []);

  const updateBedSelection = useCallback(
    (numBeds: number) => {
      setSelectedUnit(null);

      if (!allBedsSelected && selectedBedCounts.length === 1 && selectedBedCounts[0] === numBeds) {
        setSelectedBedCounts([]);
        setAllBedsSelected(true);
        return;
      }

      setSelectedBedCounts([numBeds]);
      setAllBedsSelected(false);
    },
    [allBedsSelected, selectedBedCounts],
  );

  const handleUnitSelect = useCallback(
    (unitZpid: string) => {
      if (!unitZpid) {
        setSelectedUnit(null);
        selectAllBeds();
        return;
      }

      const unit = allUnits.find((u) => u.unitZpid === unitZpid) ?? null;
      setSelectedUnit(unit);
      if (unit) {
        setAllBedsSelected(false);
        setSelectedBedCounts([unit.numBeds]);
      }
    },
    [allUnits, selectAllBeds],
  );

  useEffect(() => {
    if (!setSelectorOverrides) return;

    let newOverrides: Array<Selector> = [];

    if (selectedUnit) {
      newOverrides = [selectedUnit.selector];
    } else if (!allBedsSelected && selectedBedCounts.length > 0) {
      newOverrides = bedSelectors
        .filter((bed: BedSelector) => bed.numBeds !== undefined && selectedBedCounts.includes(bed.numBeds))
        .map((bed: BedSelector) => bed.selector);
    }

    setSelectorOverrides(newOverrides);
  }, [selectedUnit, selectedBedCounts, allBedsSelected, bedSelectors, setSelectorOverrides]);

  const hasMultipleBedTypes = bedOptions.length > 1;
  const hasMultipleUnits = allUnits.length > 1;

  if (!hasMultipleBedTypes && !hasMultipleUnits) return null;

  const dropdownPlaceholder = (() => {
    const count = filteredUnits.length;
    if (count === 0) return 'No units available';
    if (count === 1) return '1 unit available';
    return `${count} units available`;
  })();

  return (
    <div className={styles.container} data-testid="unit-picker">
      {hasMultipleBedTypes && (
        <div className={styles.pillsContainer}>
          <button className={allBedsSelected ? styles.pillSelected : styles.pill} onClick={selectAllBeds}>
            All
          </button>
          {bedOptions.map((bed) => (
            <button
              key={bed.numBeds}
              className={
                !allBedsSelected && selectedBedCounts.includes(bed.numBeds) ? styles.pillSelected : styles.pill
              }
              onClick={() => updateBedSelection(bed.numBeds)}
            >
              {bed.selector?.displayName ?? getBedDisplayName(bed.numBeds)}
            </button>
          ))}
        </div>
      )}

      {allUnits.length > 0 && (
        <div>
          <div className={styles.dropdownLabel}>Choose a unit*</div>
          <select
            className={styles.dropdown}
            value={selectedUnit?.unitZpid ?? ''}
            onChange={(e) => handleUnitSelect(e.target.value)}
            aria-label="Choose a unit"
          >
            <option value="">{dropdownPlaceholder}</option>
            {hasMultipleBedTypes && (allBedsSelected || selectedBedCounts.length !== 1)
              ? Object.entries(
                  filteredUnits.reduce<Record<string, typeof filteredUnits>>((acc, unit) => {
                    const key = String(unit.numBeds);
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(unit);
                    return acc;
                  }, {}),
                )
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([bedCount, units]) => (
                    <optgroup key={bedCount} label={Number(bedCount) === 0 ? 'Studio' : `${bedCount} bedroom`}>
                      {units.map((unit) => (
                        <option key={unit.unitZpid} value={unit.unitZpid}>
                          {unit.selector.displayName || getBedDisplayName(unit.numBeds)}
                        </option>
                      ))}
                    </optgroup>
                  ))
              : filteredUnits.map((unit) => (
                  <option key={unit.unitZpid} value={unit.unitZpid}>
                    {unit.selector.displayName || getBedDisplayName(unit.numBeds)}
                  </option>
                ))}
          </select>
        </div>
      )}
    </div>
  );
};
